import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

const BUCKET_NAME = 'task-attachments';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip', 'mp4', 'mp3', 'wav', 'ogg'];

interface UploadResult {
  path: string;
  url: string;
}

export async function uploadFile(
  taskId: string,
  file: File
): Promise<UploadResult> {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size exceeds limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
  }

  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    throw new Error(`File type ".${ext}" is not allowed`);
  }

  const { data: { user } } = await withRetry(() =>
    supabase.auth.getUser()
  );
  if (!user) throw new Error('Not authenticated');

  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext || 'bin'}`;
  const filePath = `${user.id}/${taskId}/${fileName}`;

  const { error } = await withRetry(() =>
    supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file)
  );

  if (error) throw error;

  const { data: urlData } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(filePath);

  return {
    path: filePath,
    url: urlData.publicUrl,
  };
}

export async function getFileUrl(path: string): Promise<string> {
  const { data, error } = await withRetry(() =>
    supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(path, 3600) // 1 hour expiry
  );

  if (error) throw error;
  return data.signedUrl;
}

export async function deleteFile(path: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.storage
      .from(BUCKET_NAME)
      .remove([path])
  );

  if (error) throw error;
}

export async function listFiles(taskId: string): Promise<Array<{ name: string; path: string; size: number; createdAt: string }>> {
  const { data: { user } } = await withRetry(() =>
    supabase.auth.getUser()
  );
  if (!user) throw new Error('Not authenticated');

  const prefix = `${user.id}/${taskId}/`;
  const { data, error } = await withRetry(() =>
    supabase.storage
      .from(BUCKET_NAME)
      .list(prefix)
  );

  if (error) throw error;

  return (data || []).map(file => ({
    name: file.name,
    path: `${prefix}${file.name}`,
    size: file.metadata?.size || 0,
    createdAt: file.created_at,
  }));
}
